/* eslint-disable formatjs/no-literal-string-in-jsx */

'use client';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';

import { DropCarousel } from '~/components/drop-carousel';
import { ROUTES } from '~/constants/routes';
import { useBrandControllerGetAllBrandsInfinite } from '~/lib/api';
import { withInfiniteQueryDefaults } from '~/lib/infinite-query';

import { EmptyDropsState } from '~/components';
import { useHomeDrops } from '~/hooks/use-home-drops';
import { WalletConnectButton } from './components/wallet-connect-button';
import { BrandCarousel } from './components/brand-carousel';
import { DirectSaleOffersSection } from './components/direct-sale-offers-section';
import { PackResultsStep } from '~/module/pack-opening/steps/pack-results-step';
import { usePackNotificationStore } from '~/store/purchase-store';
import { useRouter } from 'next/navigation';
import { addSearchParams } from '~/lib/search-params';
import { FeatureFlag, useFeatureFlags } from '~/providers/feature-flag-client-provider';
import { match } from 'ts-pattern';
import { HomePackGridSection } from '~/module/home/components/home-pack-grid-section';
import { HomeFinishedSalesSection } from '~/module/home/components/home-finished-sales-section';
import { SectionTitle } from '~/module/home/components/section-title';
import { useQueryClient } from '@tanstack/react-query';

const HomeContentInner = () => {
  const minimumFeatured = 1;
  const { shownDrops, isLoading: isDropsLoading } = useHomeDrops();
  const router = useRouter();

  const { isFeatureEnabled } = useFeatureFlags();
  const bottomContentType = isFeatureEnabled(FeatureFlag.HOME_FINISHED_SALES) ? 'FINISHED_SALES' : 'PACK_GRID';

  // Simplified pack notification store for displaying delivered packs
  const { deliveredPacks } = usePackNotificationStore();

  // Get the first available pack for display (showing sequentially)
  const firstAvailablePack = deliveredPacks.length > 0 ? deliveredPacks[0]?.packId : null;

  const brandParams = useMemo(() => ({ limit: 20, minimumFeatured }), [minimumFeatured]);
  const brandQueryOptions = useMemo(() => ({ query: withInfiniteQueryDefaults({}) }), []);

  const queryClient = useQueryClient();

  const {
    data: brands,
    isLoading: brandsIsLoading,
    fetchNextPage: fetchNextBrandsPage,
    hasNextPage: hasNextBrandsPage,
    isFetchingNextPage: isFetchingNextBrandsPage,
  } = useBrandControllerGetAllBrandsInfinite(brandParams, brandQueryOptions);

  if (firstAvailablePack) {
    return (
      <PackResultsStep
        key={firstAvailablePack}
        packId={firstAvailablePack}
        onClose={() => {
          void queryClient.invalidateQueries();
          router.replace(addSearchParams(ROUTES.MY_COLLECTIONS, { packId: firstAvailablePack }));
        }}
      />
    );
  }

  if (isDropsLoading) {
    return <div className="fixed size-full flex justify-center items-center">Loading...</div>;
  }
  return (
    <>
      <WalletConnectButton />
      <div className="relative overflow-hidden overscroll-none max-w-[calc(100vw)]">
        {shownDrops.length > 0 ? (
          /* Hero Carousel Section */
          <DropCarousel shownDrops={shownDrops} />
        ) : (
          /* Empty State */
          <div className="relative w-full pt-24">
            <EmptyDropsState className="h-full" />
          </div>
        )}
      </div>
      <div className="px-4">
        <DirectSaleOffersSection />
      </div>
      <div className="px-4 pb-20 mt-4">
        <SectionTitle href={ROUTES.COLLECTIONS} title="Collections" />

        <BrandCarousel
          brands={brands?.pages.flatMap((page) => page.data.data)}
          hasNextPage={hasNextBrandsPage}
          isFetchingNextPage={isFetchingNextBrandsPage}
          isLoading={brandsIsLoading}
          onLoadMore={() => {
            void fetchNextBrandsPage();
          }}
        />

        <div className="h-10" />

        {match(bottomContentType)
          .with('FINISHED_SALES', () => <HomeFinishedSalesSection />)
          .with('PACK_GRID', () => <HomePackGridSection />)
          .exhaustive()}
      </div>
    </>
  );
};

export const HomeContent = dynamic(() => Promise.resolve(HomeContentInner), {
  ssr: false,
});
